import { useState, useMemo } from "react";
import Tabs from "../tabs";
import dayjs from "dayjs";
import {
  Box, Button,
  Typography,
  Stepper,
  Step,
  StepLabel,
  StepConnector,
  Stack,
  Grid,
  Card,
  IconButton,
  TextField,
  Divider,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import CheckIcon from "@mui/icons-material/Check";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import SingleSelect from "../../../../components/selections/singleSelect";
import SnackbarMessage from "../../../../components/snackBar/snackBar";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import DeleteOutlineIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";


const initialSlots = [
  { id: 1, time: "10 am to 12 pm", count: 5 },
  { id: 2, time: "1 pm to 3 pm", count: 5 },
  { id: 3, time: "3 pm to 5 pm", count: 5 },
  { id: 4, time: "5 pm to 7 pm", count: 5 },
  { id: 5, time: "11:48 am to 1:48 pm", count: 5 },
];

const departments = [
  { value: 1, label: "Cardiology" },
  { value: 2, label: "Neurology" },
  { value: 3, label: "Orthopedics" },
];

const specializations = [
  { value: 1, departmentId: 1, label: "Interventional Cardiology" },
  { value: 2, departmentId: 1, label: "Pediatric Cardiology" },
  { value: 3, departmentId: 2, label: "Neuro Surgery" },
  { value: 4, departmentId: 2, label: "Clinical Neurology" },
  { value: 5, departmentId: 3, label: "Joint Replacement" },
];

const doctorDetails = [
  { value: 1, label: "Sumit", specializationID: 1 },
  { value: 2, label: "Ramesh", specializationID: 2 },
];


const PurpleConnector = styled(StepConnector)(({ theme }) => ({
  [`& .MuiStepConnector-line`]: {
    height: 3,
    border: 0,
    backgroundColor: theme.palette.grey[400],
    borderRadius: 1,
  },
}));

const StepIconRoot = styled("div")(({ ownerState }) => ({
  backgroundColor: ownerState.active || ownerState.completed ? "#9c27b0" : "#fff",
  border: "2px solid",
  borderColor: ownerState.active || ownerState.completed ? "#9c27b0" : "#bdbdbd",
  color: ownerState.active || ownerState.completed ? "#fff" : "#9e9e9e",
  width: 48,
  height: 48,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

function CustomStepIcon({ active, completed, icon }) {
  const icons = {
    1: <MedicalServicesIcon />,
    2: <CheckIcon />,
  };
  return (
    <StepIconRoot ownerState={{ active, completed }}>
      {icons[String(icon)]}
    </StepIconRoot>
  );
}

/* ---------- MAIN COMPONENT ---------- */

function AssignSlotCreation() {
  const [state, setState] = useState({
    activeStep: 0,
    steps: ["Slot Creation", "Confirmation"],
    selectedDepartmentValue: "",
    selectedSpecializationValue: "",
    selectedEmpValue: "",
    selectedAveilableDate: dayjs(),
    errors: {},
  });

  const {
    activeStep,
    steps,
    selectedDepartmentValue,
    selectedSpecializationValue,
    selectedEmpValue,
    selectedAveilableDate,
    errors,
  } = state;

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });


  const specializationOptions = useMemo(
    () =>
      specializations.filter(
        (s) => s.departmentId === selectedDepartmentValue
      ),
    [selectedDepartmentValue]
  );

  const doctorOptions = useMemo(
    () =>
      doctorDetails.filter(
        (d) => d.specializationID === selectedSpecializationValue
      ),
    [selectedSpecializationValue]
  );


  const handleSelectChange = (name, value) => {
    setState((prev) => ({
      ...prev,
      [name]: value,
      errors: { ...prev.errors, [name]: "" },
    }));
  };

  const [slots, setSlots] = useState(initialSlots);

  const updateCount = (id, value) => {
    setSlots((prev) =>
      prev.map((slot) =>
        slot.id === id
          ? { ...slot, count: Math.max(0, Math.min(200, value)) }
          : slot
      )
    );
  };

  const deleteSlot = (id) => {
    setSlots((prev) => prev.filter((slot) => slot.id !== id));
  };

  const step0Fields = [
    {
      type: "select",
      name: "selectedDepartmentValue",
      label: "Select Branch Dept",
      value: selectedDepartmentValue,
      options: departments,
    },
    {
      type: "select",
      name: "selectedSpecializationValue",
      label: "Select Specialization",
      value: selectedSpecializationValue,
      options: specializationOptions,
      disabled: !selectedDepartmentValue,
    },
    {
      type: "select",
      name: "selectedEmpValue",
      label: "Select Doctor",
      value: selectedEmpValue,
      options: doctorOptions,
      disabled: !selectedSpecializationValue,
    },
    {
      type: "date",
      name: "selectedAveilableDate",
      label: "Available Date",
      value: selectedAveilableDate,
    },
  ];


  const styles = {
    border: "none",
    borderRadius: "8px",
    px: 3,
    py: 1,
    "&.Mui-disabled": {
      cursor: "not-allowed",          // ✅ force not-allowed cursor
      opacity: 0.7,                   // optional visual cue
      color: "var(--light-bg)",       // keep text same
    },
  };

  const handleBack = () => {
    setState((prev) => ({
      ...prev,
      activeStep: Math.max(prev.activeStep - 1, 0),
    }));
  };

  const handleNext = async (e) => {
    e.preventDefault();
    let extraValidations = [];

    if (activeStep === 0) {
      extraValidations = [
        { valid: selectedDepartmentValue, field: "selectedDepartmentValue", message: "Please select department" },
        { valid: selectedSpecializationValue, field: "selectedSpecializationValue", message: "Please select specialization" },
        { valid: selectedEmpValue, field: "selectedEmpValue", message: "Please select Employee" },
        { valid: selectedAveilableDate, field: "selectedAveilableDate", message: "Please select Date" },
      ];
    }

    for (let rule of extraValidations) {
      if (!rule.valid) {
        setSnackbar({ open: true, message: rule.message, severity: "error" });
        setState((prev) => ({ ...prev, errors: { ...prev.errors, [rule.field]: rule.message } }));
        return;
      }
    }

    setState((prev) => ({
      ...prev,
      activeStep: prev.activeStep + 1,
    }));

  };

  return (
    <div>
      <Tabs />

      <Box mt={3}>
        <Stepper activeStep={activeStep} alternativeLabel connector={<PurpleConnector />}>
          {steps.map((label, index) => (
            <Step key={label}>
              <StepLabel StepIconComponent={CustomStepIcon}>
                <Typography fontSize={14}>{label}</Typography>
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      {activeStep === 0 ?
        (<Box
          sx={{
            width: "100%",
            borderRadius: 2,
            border: "1px solid #e0e0e0",
            overflow: "hidden",
            bgcolor: "#fff",
            p: 3,
            mt: 2
          }}
        >
          {/* 🔹 Header */}
          <Box
            sx={{
              px: 2,
              py: 1,
              color: "#000",
              fontWeight: 600,
              fontSize: "14px",
            }}
          >
            <Grid container spacing={1}>
              {step0Fields.map((field) => (
                <Grid item size={{ xs: 12, md: 3 }}>
                  {field.type === "select" ? (
                    <SingleSelect
                      key={field.name}
                      name={field.name}
                      label={field.label}
                      options={field.options}
                      value={field.value}
                      onChange={handleSelectChange}
                      error={!!errors[field.name]}
                      disabled={field.disabled}
                    />
                  ) : (
                    <LocalizationProvider key={field.name} dateAdapter={AdapterDayjs}>
                      <DatePicker
                        disablePast
                        label={field.label}
                        value={field.value}
                        onChange={(newValue) =>
                          handleSelectChange(field.name, newValue)
                        }
                        slotProps={{
                          textField: { size: "small", fullWidth: true },
                          openPickerButton: {
                            sx: {
                              color: "green",
                              "& svg": {
                                fontSize: "32px", // 👈 icon size
                              },
                            },
                          },
                        }}
                      />
                    </LocalizationProvider>
                  )}
                </Grid>
              ))}
            </Grid>
          </Box>

          <Divider />

          {selectedDepartmentValue &&
            selectedSpecializationValue &&
            selectedEmpValue &&
            selectedAveilableDate ? (<Grid container spacing={1} mt={1}>
              {slots.map((slot, index) => (
                <Grid item size={{ xs: 12, md: 6 }} key={slot.id}>
                  <Card
                    sx={{
                      p: 2.5,
                      borderRadius: 3,
                      background:
                        "linear-gradient(135deg, #ffffff, #f9f9ff)",
                      boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
                      border: "1px solid rgba(0,0,0,0.15)",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: "0 12px 30px rgba(0,0,0,0.15)",
                      },
                    }}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      {/* Left */}
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Typography fontWeight={600}>
                          {index + 1}.
                        </Typography>
                        <PersonOutlineIcon sx={{ color: "#7b1fa2" }} />
                        <Typography fontWeight={500}>
                          {slot.time}
                        </Typography>
                      </Stack>

                      {/* Counter */}
                      <Stack
                        direction="row"
                        alignItems="center"
                        sx={{
                          border: "2px solid #7b1fa2",
                          borderRadius: 3,
                          px: 1,
                          py: 0.5,
                          backgroundColor: "#fff",
                        }}
                      >
                        <IconButton
                          size="small"
                          onClick={() =>
                            updateCount(slot.id, slot.count - 1)
                          }
                        >
                          <RemoveIcon />
                        </IconButton>

                        <TextField
                          value={slot.count}
                          onChange={(e) =>
                            updateCount(
                              slot.id,
                              Number(e.target.value) || 0
                            )
                          }
                          size="small"
                          variant="standard"
                          InputProps={{
                            disableUnderline: true,
                            inputProps: {
                              style: {
                                textAlign: "center",
                                width: 35,
                                fontWeight: 600,
                              },
                            },
                          }}
                        />

                        <IconButton
                          size="small"
                          onClick={() =>
                            updateCount(slot.id, slot.count + 1)
                          }
                        >
                          <AddIcon />
                        </IconButton>
                      </Stack>

                      {/* Delete */}
                      <IconButton
                        onClick={() => deleteSlot(slot.id)}
                        sx={{
                          color: "#f44336",
                          "&:hover": {
                            backgroundColor: "#ffeaea",
                          },
                        }}
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                    </Stack>
                  </Card>
                </Grid>
              ))}
            </Grid>) : ""}

        </Box>) : (
          <Box
            sx={{
              width: "100%",
              borderRadius: 2,
              border: "1px solid #e0e0e0",
              overflow: "hidden",
              bgcolor: "#fff",
              mt: 2
            }}
          >
            {/* 🔹 Header */}
            <Box
              sx={{
                px: 2,
                py: 1,
                color: "#000",
                fontWeight: 600,
                fontSize: "14px",
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                <Typography variant="h6">
                  Appointment Summary
                </Typography>

                <Button variant="outlined" size="small">
                  Download Pdf
                </Button>
              </Box>
              <Divider />
              <Grid container spacing={1} mt={1}>
                {[
                  {
                    label: "Department",
                    value:
                      departments.find((item) => item.value === selectedDepartmentValue)
                        ?.label || "-",
                  },
                  {
                    label: "Specialization",
                    value:
                      specializations.find(
                        (item) => item.value === selectedSpecializationValue
                      )?.label || "-",
                  },
                  {
                    label: "Doctor",
                    value:
                      doctorDetails.find((item) => item.value === selectedEmpValue)?.label ||
                      "-",
                  },
                  {
                    label: "Appointment Date",
                    value: selectedAveilableDate
                      ? dayjs(selectedAveilableDate).format("DD MMM YYYY")
                      : "-",
                  },
                  {
                    label: "Appointment Slots",
                    isSlot: true,
                    value: slots,
                  },
                ].map((item, index) => (
                  <Grid item size={{ xs: 12, md: 6 }} key={index}>
                    <Box>
                      <Typography fontWeight={600}>{item.label}:</Typography>

                      {/* SLOT LIST */}
                      {item.isSlot ? (
                        <ul style={{ margin: "6px 0 0 18px", padding: 0 }}>
                          {item.value.length > 0 ? (
                            item.value.map((slot, i) => (
                              <li key={i}>
                                <Typography color="text.secondary">
                                  {slot.time} — Available: <span style={{ fontWeight: 600, fontStyle: "bold" }}>{slot.count}</span>
                                </Typography>
                              </li>
                            ))
                          ) : (
                            <Typography color="text.secondary">No slots</Typography>
                          )}
                        </ul>
                      ) : (
                        <Typography color="text.secondary">{item.value}</Typography>
                      )}
                    </Box>
                  </Grid>
                ))}
              </Grid>

            </Box>
          </Box>
        )}


      <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 2 }}>
        <div style={{ cursor: activeStep === 0 ? "not-allowed" : "pointer" }}>
          <Button disabled={activeStep === 0} variant="outlined" onClick={handleBack}
            sx={{
              ...styles,
              background: "var(--disabled-color)",
              color: "#000",
              "&:hover": {
                background: "var(--primary)",
                color: "#fff"
              },
              "&.Mui-disabled": {
                cursor: "not-allowed",          // ✅ force not-allowed cursor
                opacity: 0.7,                   // optional visual cue
                color: "#000",       // keep text same
              },
            }}>
            Back
          </Button>
        </div>
        <Button
          variant="contained"
          color="primary"
          disabled={activeStep >= 3}
          onClick={handleNext}
          sx={{ ...styles, background: "var(--primary)", color: "var(--light-bg)", }}
        >
          Next
        </Button>
      </Stack>

      <SnackbarMessage
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      />
    </div>
  );
};
export default AssignSlotCreation;